import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { forkJoin, of } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';

import { ShellDomainProvider } from '../utility/shell-domain.provider';
import { UtilityService } from '../utility/utility.service';
import { PlatformDataService } from '../pds/platform-data.service';
import { SqlQueryBuilderService } from '../query-builder/sql-query-builder.service';
import {EventDataAction} from '../pds/platform-data.service';

const header: any = new HttpHeaders({
    'Content-Type': 'application/json'
});

@Injectable()
export class AggregateService {

    private getSqlFilterUrl: string = ShellDomainProvider['api'].DataCore + 'DataManipulationQuery/GetBySQLFilter';
    private getRowLevelSecurityUrl: string = ShellDomainProvider['api'].DataCore + 'DataManipulationQuery/GetRowLevelSecurity';
    private getConnectionUrl: string = ShellDomainProvider['api'].DataCore + 'DataManipulationQuery/GetConnections';
    private insertUrl: string = ShellDomainProvider['api'].DataCore + 'DataManipulationCommand/Insert';
    private updateUrl: string = ShellDomainProvider['api'].DataCore + 'DataManipulationCommand/Update';
    private deleteUrl: string = ShellDomainProvider['api'].DataCore + 'DataManipulationCommand/Delete';
    private changeSecurityPermissionUrl: string = ShellDomainProvider['api'].DataCore + 'DataManipulationCommand/ChangeSecurityPermissionWithRowLevelSecurity';
    private connectUrl: string = ShellDomainProvider['api'].DataCore + 'DataManipulationCommand/Connect';
    private disConnectUrl: string = ShellDomainProvider['api'].DataCore + 'DataManipulationCommand/Disconnect';
    private getDistinctUrl: string = ShellDomainProvider['api'].DataCore + 'DataManipulationQuery/GetDistinct';
    private getDistinctCountUrl: string = ShellDomainProvider['api'].DataCore + 'DataManipulationQuery/GetDistinctCount';
    private getPresignedUrl: string = ShellDomainProvider['api'].BusinessService + 'DeltaService/Storage/GetPreSignedUrlForUploadByRolePriority';
    private getAggregateSum: string = ShellDomainProvider['api'].DataCore + 'DataManipulationQuery/GetAggregateSum';
    private countryDenormalizedCollection = Object.create(null);
    private bankDenormalizedCollection = Object.create(null);
    private budgetDenormalizedCollection = Object.create(null);
    private workflorFormTypeDenormalizedCollection = Object.create(null);
    private sendMailUrl: string = ShellDomainProvider['api'].EmailService + '/MailCommand/SendEmail';
    private workflowInstanceUrl: string = ShellDomainProvider['api'].WorkflowService + 'WorkflowCommand/CreateWorkflowInstance';
    private deleteFileUrl: string = ShellDomainProvider['api'].Storage + 'StorageCommand/DeleteFile';
    private deleteFilesUrl: string = ShellDomainProvider['api'].Storage + 'StorageCommand/DeleteAll';
    private varyUrl: string = ShellDomainProvider['api'].DataCore + 'DataManipulationCommand/Vary';
    private executeFileConversionPipelineUrl: string = ShellDomainProvider['api'].Storage + 'StorageCommand/ExecuteFileConversionPipeline';

    constructor(
        private http: HttpClient,
        private platformDataService: PlatformDataService,
        private sqlQueryBuilderService: SqlQueryBuilderService,
        private utilityService: UtilityService) {
    }

    getDictionaryDataByAggregate(sqlQuery, prop, denomCollection) {
        if (Object.keys(denomCollection).length)
            return of(denomCollection);

        let modifiedQuery = sqlQuery + " pageNumber = <0>";

        let result = [];
        let callList = [];

        return this.platformDataService.getBySqlFilter('PlatformDictionary', modifiedQuery, null).pipe(switchMap((response: Response) => {
            const firstResponse = response.body;

            if (firstResponse['StatusCode'] != 0)
                return result;

            let totalRecord = firstResponse['TotalRecordCount'];
            let firstResult = firstResponse['Results'];
            let firstResponseLength = firstResult.length;

            if (totalRecord > firstResponseLength) {
                let numberOfCall = Math.ceil(totalRecord / firstResponseLength);
                for (let index = 0; index < numberOfCall - 1; index++) {
                    let newQuery = SqlQueryBuilderService.clone(sqlQuery);
                    newQuery = newQuery + " pageNumber = <" + (index + 1) + ">";
                    let model = {
                        EntityName: 'PlatformDictionary',
                        Text: newQuery
                    }
                    callList.push(this.aggregateSqlFilterModel(model));
                }
            }

            if (callList.length > 0) {
                result = firstResult.concat([]);
                const pages$ = callList.map(m => this.platformDataService.getBySqlFilter('PlatformDictionary', JSON.parse(m.Payload).Text, null));
                return forkJoin(pages$).pipe(map((responses: any[]) => {
                    responses.forEach(r => { if (r.body?.Results?.length) result = result.concat(r.body.Results); });
                    return this.getPlatformDictionaryProcessedData(result, prop, denomCollection);
                }));
            }

            return of(this.getPlatformDictionaryProcessedData(firstResult, prop, denomCollection));

        })).pipe(map((responseList: any) => responseList));
    }

    private getPlatformDictionaryProcessedData(responseResults, prop, denomCollection) {
        let results = responseResults.sort(function (a, b) {
            let aValue = a["Value"];
            let bValue = b["Value"];
            if (typeof aValue == 'string') {
                aValue = JSON.parse(aValue);
            }

            if (typeof bValue == 'string') {
                bValue = JSON.parse(bValue);
            }

            if (aValue[prop] > bValue[prop])
                return 1;
            if (aValue[prop] < bValue[prop])
                return -1;
            return 0;
        });


        for (let i = 0; i < results.length; i++) {
            denomCollection[results[i]["Key"]] = (typeof results[i]["Value"] == 'string') ? JSON.parse(results[i]["Value"]) : results[i]["Value"];
        }

        return denomCollection;
    }

    prepareAggregateSqlFilterModel(property, operator, itemId, entityName, fields, pageNumber?, pageSize?) {
        const pn = pageNumber || 0;
        const ps = pageSize || 100;
        const query = this.sqlQueryBuilderService.prepareSqlFilterModel(property, operator, itemId, entityName, fields, pn, ps);

        let model = {
            EntityName: entityName,
            Text: query
        }

        return model;
    }

    aggregateSqlFilterModel(filterModel) {
        let model =
        {
            "Uri": this.getSqlFilterUrl,
            "Verb": "Post",
            "Payload": JSON.stringify(filterModel),
            "SuccessIf": JSON.stringify({ StatusCode: 0 })
        }
        return model;
    }

    aggregateDistinctModel(filterModel) {
        let model =
        {
            "Uri": this.getDistinctUrl,
            "Verb": "Post",
            "Payload": JSON.stringify(filterModel),
            "SuccessIf": JSON.stringify({ StatusCode: 0 })
        }
        return model;
    }

    aggregateDistinctCountModel(filterModel) {
        let model =
        {
            "Uri": this.getDistinctCountUrl,
            "Verb": "Post",
            "Payload": JSON.stringify(filterModel),
            "SuccessIf": JSON.stringify({ StatusCode: 0 })
        }
        return model;
    }

    aggregateSumSqlFilterModel(filterModel) {
        let model =
        {
            "Uri": this.getAggregateSum,
            "Verb": "Post",
            "Payload": JSON.stringify(filterModel),
            "SuccessIf": JSON.stringify({ StatusCode: 0 })
        }
        return model;
    }

    aggregateGetConnectionModel(connectionModel) {
        let model =
        {
            "Uri": this.getConnectionUrl,
            "Verb": "Post",
            "Payload": JSON.stringify(connectionModel),
            "SuccessIf": JSON.stringify({ StatusCode: 0 })
        }
        return model;
    }

    aggregateConnectModel(connectionModel) {
      if(ShellDomainProvider['api'] && ShellDomainProvider['api'].IsEnableEventListeningForAll) {
        let eventDataAction = <EventDataAction> ShellDomainProvider['api'] && ShellDomainProvider['api'].eventDataAction? ShellDomainProvider['api'].eventDataAction.Connect : "Connected";
        connectionModel['EventData'] = {EventType: "Connection" + "." + eventDataAction}
      }
        let model =
        {
            "Uri": this.connectUrl,
            "Verb": "Post",
            "Payload": JSON.stringify(connectionModel),
            "SuccessIf": JSON.stringify({ StatusCode: 0 })
        }
        return model;
    }

    aggregateDisConnectModel(disConnectionModel) {
      if(ShellDomainProvider['api'] && ShellDomainProvider['api'].IsEnableEventListeningForAll) {
        let eventDataAction = <EventDataAction> ShellDomainProvider['api'] && ShellDomainProvider['api'].eventDataAction? ShellDomainProvider['api'].eventDataAction.Disconnect : "Disconnected";
        disConnectionModel['EventData'] = {EventType: "Connection" + "." + eventDataAction}
      }
        let model =
        {
            "Uri": this.disConnectUrl,
            "Verb": "Post",
            "Payload": JSON.stringify(disConnectionModel),
            "SuccessIf": JSON.stringify({ StatusCode: 0 })
        }
        return model;
    }

    aggregateInsertModel(insertModel) {
        let model = {
            "Uri": this.insertUrl,
            "Verb": "Post",
            "Payload": JSON.stringify(insertModel),
            "SuccessIf": JSON.stringify({ StatusCode: 0 })
        };

        return model;
    }

    aggregateUpdateModel(updateModel) {
        let model =
        {
            "Uri": this.updateUrl,
            "Verb": "Post",
            "Payload": JSON.stringify(updateModel),
            "SuccessIf": JSON.stringify({ StatusCode: 0 })
        }
        return model;
    }

    aggregateDeleteModel(updateModel) {
        let model =
        {
            "Uri": this.deleteUrl,
            "Verb": "Post",
            "Payload": JSON.stringify(updateModel),
            "SuccessIf": JSON.stringify({ StatusCode: 0 })
        }
        return model;
    }

    aggregateGetRowLevelSecurityModel(securityModel) {
        let model =
        {
            "Uri": this.getRowLevelSecurityUrl,
            "Verb": "Post",
            "Payload": JSON.stringify(securityModel),
            "SuccessIf": JSON.stringify({ StatusCode: 0 })
        }
        return model;
    }

    aggregatePermissionUpdateModel(premissionUpdateModel) {
        let model =
        {
            "Uri": this.changeSecurityPermissionUrl,
            "Verb": "Post",
            "Payload": JSON.stringify(premissionUpdateModel),
            "SuccessIf": JSON.stringify({ StatusCode: 0 })
        }
        return model;
    }


    getAllDataForConnectionByAggregate(connectionModel) {
        connectionModel["EntityName"] = 'Connection';
        connectionModel["PageNumber"] = 0;

        let result = [];
        let callList = [];

        return this.platformDataService.getConnections(connectionModel).pipe(switchMap((response: Response) => {
            const firstResponse = response.body;

            if (firstResponse['StatusCode'] != 0)
                return result;

            const totalRecord = firstResponse['TotalRecordCount'];
            const firstResult = firstResponse['Results'];
            const firstResponseLength = firstResult.length;

            if (totalRecord > firstResponseLength) {
                let numberOfCall = Math.ceil(totalRecord / firstResponseLength);
                for (let index = 0; index < numberOfCall - 1; index++) {
                    let newModel = SqlQueryBuilderService.clone(connectionModel);
                    newModel["PageNumber"] = index + 1;
                    callList.push(this.aggregateGetConnectionModel(newModel));
                }
            }

            if (callList.length > 0) {
                result = firstResult.concat([]);
                const pages$ = callList.map(m => this.platformDataService.getConnections(JSON.parse(m.Payload)));
                return forkJoin(pages$).pipe(map((responses: any[]) => {
                    responses.forEach(r => { r.body?.Results?.forEach(item => result.push(item)); });
                    return result;
                }));
            }

            return of(firstResult);

        })).pipe(map((responseList: any) => responseList));
    }

    getEntityAllDataByAggregate(entityName, sqlFilterObj) {

        let originalSQLFilter = SqlQueryBuilderService.clone(sqlFilterObj);
        if (sqlFilterObj.PageNumber>=0) {
            sqlFilterObj.Text += " pageNumber = <" + sqlFilterObj.PageNumber + ">";
        }

        if (sqlFilterObj.PageSize) {
            sqlFilterObj.Text += " pageSize = <" + sqlFilterObj.PageSize + ">";
        }

        let result = [];
        let callList = [];

        return this.platformDataService.getBySqlFilter(entityName, sqlFilterObj.Text, null).pipe(switchMap((response: Response) => {
            const firstResponse = response.body;

            if (firstResponse['StatusCode'] != 0)
                return result;

            let totalRecord = firstResponse['TotalRecordCount'];
            let firstResult = firstResponse['Results'];
            let firstResponseLength = firstResult.length;

            if (totalRecord > firstResponseLength) {
                let numberOfCall = Math.ceil(totalRecord / firstResponseLength);
                for (let index = 0; index < numberOfCall - 1; index++) {
                    let newModel = SqlQueryBuilderService.clone(originalSQLFilter);
                    newModel["EntityName"] = entityName;
                    newModel["Text"] += " pageNumber = <" + (index + 1) + ">";
                    newModel["Text"] += " pageSize = <" + newModel.PageSize + ">";

                    delete newModel.PageNumber;
                    delete newModel.PageSize;

                    callList.push(this.aggregateSqlFilterModel(newModel));
                }
            }

            if (callList.length > 0) {
                result = firstResult.concat([]);
                const pages$ = callList.map(m => this.platformDataService.getBySqlFilter(entityName, JSON.parse(m.Payload).Text, null));
                return forkJoin(pages$).pipe(map((responses: any[]) => {
                    responses.forEach(r => { if (r.body?.Results) result = result.concat(r.body.Results); });
                    return result;
                }));
            }

            return of(firstResult);
        })).pipe(map((responseList: any) => responseList));
    }

    getAllDataByAggregateDistinct(entityName, distinctProperty, filterQuery?) {
        let sqlFilterObj = {};
        sqlFilterObj["EntityName"] = entityName;
        sqlFilterObj["DistinctPropertyName"] = distinctProperty;
        sqlFilterObj["PageNumber"] = 0;
        sqlFilterObj["PageLimit"] = 100;
        sqlFilterObj["FilterQuery"] = filterQuery;

        let result = [];
        let callList = [];

        return this.platformDataService.getDistinct(entityName, distinctProperty, null, filterQuery).pipe(switchMap((response: Response) => {
            const firstResponse = response.body;
            if (firstResponse['StatusCode'] != 0)
                return result;

            let totalRecord = firstResponse['TotalRecordCount'];
            let firstResult = firstResponse['Results'];
            let firstResponseLength = firstResult.length;

            if (totalRecord > firstResponseLength) {
                let numberOfCall = Math.ceil(totalRecord / firstResponseLength);
                for (let index = 0; index < numberOfCall - 1; index++) {
                    sqlFilterObj["EntityName"] = entityName;
                    sqlFilterObj["DistinctPropertyName"] = distinctProperty;
                    sqlFilterObj["PageLimit"] = 100;
                    sqlFilterObj["PageNumber"] = index + 1;
                    sqlFilterObj["FilterQuery"] = filterQuery;
                    callList.push(this.aggregateDistinctModel(sqlFilterObj));
                }
            }

            if (callList.length > 0) {
                result = firstResult.concat([]);
                const pages$ = callList.map(m => this.platformDataService.getDistinct(entityName, distinctProperty, null, filterQuery));
                return forkJoin(pages$).pipe(map((responses: any[]) => {
                    responses.forEach(r => { if (r.body?.Results) result = result.concat(r.body.Results); });
                    return result;
                }));
            }

            return of(firstResult);
        })).pipe(map((responseList: any) => responseList));
    }

    getAllCountryDataByAggregate() {
        let sqlQuery = "select<Key,Value> from<PlatformDictionary> where<Name=__eql(CountryCode)>";

        return this.getDictionaryDataByAggregate(sqlQuery, 'country', this.countryDenormalizedCollection);
    }

    getAllCountryCityStateAggregate() {
        let sqlQuery = "select<Key,Value> from<PlatformDictionary> where<Name=__eql(CountryCityState)>";
        return this.getDictionaryDataByAggregate(sqlQuery, 'country', this.countryDenormalizedCollection);
    }

    getAllBankDataByAggregate() {
        let sqlQuery = "select<Key,Value> from<PlatformDictionary> where<Name=__eql(BankName)>";

        return this.getDictionaryDataByAggregate(sqlQuery, 'bank', this.bankDenormalizedCollection)
    }

    getAllWorkflowFormTypes() {
        let sqlQuery = "select<Key,Value> from<PlatformDictionary> where<Name=__eql(WorkflowFormType)>";

        return this.getDictionaryDataByAggregate(sqlQuery, 'name', this.workflorFormTypeDenormalizedCollection)
    }

    getAllDataForConnectionByAllAggregateMethod(connectionModel) {
        connectionModel["EntityName"] = 'Connection';
        connectionModel["PageNumber"] = 0;

        let result = [];

        return this.platformDataService.getConnections(connectionModel).pipe(switchMap((response: any) => {
            const firstResponse = response.body;
            if (firstResponse['StatusCode'] != 0) return of(result);
            const totalRecord = firstResponse['TotalRecordCount'];
            const firstResult = firstResponse['Results'];
            const firstResponseLength = firstResult.length;

            if (totalRecord <= firstResponseLength) return of(firstResult);

            const numberOfCall = Math.ceil(totalRecord / firstResponseLength);
            const pages$ = [];
            for (let index = 0; index < numberOfCall - 1; index++) {
                const newModel = SqlQueryBuilderService.clone(connectionModel);
                newModel["PageNumber"] = index + 1;
                pages$.push(this.platformDataService.getConnections(newModel));
            }
            result = firstResult.concat([]);
            return forkJoin(pages$).pipe(map((responses: any[]) => {
                responses.forEach(r => { r.body?.Results?.forEach(item => result.push(item)); });
                return result;
            }));
        })).pipe(map((responseList: any) => responseList));
    }

    aggregatePresignedModel(payload) {
        const enrichedPayload: Record<string, unknown> = {
            ...(JSON.parse(payload) as Record<string, unknown>),
            AllowedFeaturesToReadFile: [],
            AllowedFeaturesToUpdateFile: []
        };
        let model =
        {
            "Uri": this.getPresignedUrl,
            "Verb": "Post",
            "Payload": payload,
            "SuccessIf": JSON.stringify({ StatusCode: 0 })
        }
        return model;
    }

    getRowLevelSecurityAggrigateModel(entityName, id) {
        return this.aggregateGetRowLevelSecurityModel({
            EntityName: entityName,
            ItemId: id
        });
    }

    getConnectionModelAggregate(parentEntity, parentId, childEntity, childId, tags) {
        let connectionModel = {
            ItemId: this.utilityService.getNewGuid(),
            ParentEntityID: parentId,
            ParentEntityName: parentEntity,
            ChildEntityID: childId,
            ChildEntityName: childEntity,
            Tags: [tags]
        }
        return this.aggregateConnectModel(connectionModel);
    }

    getAggregateSumModel(entityName: string, groupBy: string, properties, filter, aggregateField: string, pageLimit?: number) {
        let model = {
            'EntityName': entityName,
            'GroupByFieldName': groupBy,
            'PageNumber': 0,
            'PageLimit': pageLimit ? pageLimit : 100,
            'FilterQuery': this.sqlQueryBuilderService.prepareQuery(entityName, properties, filter,
                null, 0, 0),
            'AggregateFieldName': aggregateField
        }

        return model;
    }

    getGQLAggregateSumModel(entityName: string, groupBy: string, filterString: string, aggregateField: string, sort?: string, pageSize?: number) {
        let model = {
            'EntityName': entityName,
            'PageNumber': 1,
            'PageSize': pageSize ? pageSize : 100,
            'Filter': filterString,
            'Group': `{_id:'$${groupBy}' , Sum :{$sum : '$${aggregateField}'}}`

        }

        if (sort) {
            model['Sort'] = sort;
        }
        return model;
    }

    aggregateSendMailModel(sendMailModel) {
        let model =
        {
            "Uri": this.sendMailUrl,
            "Verb": "Post",
            "Payload": JSON.stringify(sendMailModel),
            "SuccessIf": JSON.stringify({ StatusCode: 0 })
        }
        return model;
    }

    aggregateCreateWorkflowInstance(instanceModel) {
        let model =
        {
            "Uri": this.workflowInstanceUrl,
            "Verb": "Post",
            "Payload": JSON.stringify(instanceModel),
            "SuccessIf": JSON.stringify({ StatusCode: 0 })
        }

        return model;
    }

    aggregateDeleteFileModel(fileid) {
        let model =
        {
            "Uri": this.deleteFileUrl,
            "Verb": "Post",
            "Payload": JSON.stringify({ ItemId: fileid }),
            "SuccessIf": JSON.stringify({ StatusCode: 0 })
        }

        return model;
    }

    aggregateDeleteFilesModel(fileids) {
        let model =
        {
            "Uri": this.deleteFilesUrl,
            "Verb": "Post",
            "Payload": JSON.stringify({ ItemIds: fileids }),
            "SuccessIf": JSON.stringify({ StatusCode: 0 })
        }

        return model;
    }

    aggregateExecuteFileConversionPipelineUrlModel(pipelineCommand) {
        let model = {
            "Uri": this.executeFileConversionPipelineUrl,
            "Verb": "Post",
            "Payload": JSON.stringify(pipelineCommand),
            "SuccessIf": JSON.stringify({ StatusCode: 0 })
        }
        return model;
    }

    aggregateVaryModel(varyModel) {
        let model = {
            "Uri": this.varyUrl,
            "Verb": "POST",
            "Payload": JSON.stringify(varyModel),
            "SuccessIf": JSON.stringify({ StatusCode: 0 })
        };

        return model;
    }

}
